import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { checkExists, getParticipantURL } from '../../../api/commonHTTP';
import { selectDocumentTypes } from '../../../store/dataSelectors';
import {
    clearAppointmentType,
    loadAppointmentTypeDetails,
    loadDocumentTypes,
    loadInCludedFieldsInInitialAppointment,
    loadUserEventTypes
} from '../../../store/directusService';
import {
    createAppointmentSectionHeadings,
    createParticipantSectionHeadings,
    loadAppointmentSectionHeadings,
    loadContractConstants,
    updateAppointmentSectionHeadings,
    updateContractConstants,
    updateParticipantSectionHeadings
} from '../../../store/participantService';
import {
    addDirectusContractSelections,
    updateDirectusContractSelections,
    updatePreSelects
} from '../../../utils/directusFunctions';
import {
    NO_CONTRACT_ERROR,
    NO_UPDATE_ERROR
} from '../../../utils/formValidation/commonErrorConstants';
import {
    APPOINTMENT_TYPES_ERROR,
    CONTRACT_CONSTANTS_ERROR,
    USER_EVENT_TYPE_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys, deepEqual } from '../../../utils/objectUtils';
import { uuid } from '../../../utils/stringUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import Switch from '../../formElements/Switch';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import AutomaticAppointmentConfiguration from './AutomaticAppointmentsConfiguration';
import InitialAppointmentConfiguration from './InitialAppointmentConfiguration';
import {
    configMissingDocumentTypes,
    validateConstants,
    validateDirectusLists
} from './validateAppointmentsAdmin';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './appointmentsAdmin.module.css';

const _WARNING_TIMEOUT = 10000;
const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const submitAppointmentTypesMessage = 'Updating appointment types collection';
const submitDocumentTypesMessage = 'Document types have been updated';
const submitUserEventTypesMessage = 'Updating user event types collection';
const submitContractConstantsMessage = 'Appointment types: contract constants have been updated';
const submitSectionHeadingsMessage = 'Appointment types: section headings have been updated';
const documentTypeName = 'document_type';
const appointmentTypeName = 'calendar_appointment_type';
const userEventTypeName = 'user_event_type';

const AppointmentsAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialSectionHeadings = {
        questionnaire: false,
        jobReadiness: false,
        actionPlan: false,
        barriers: false,
        disclaimer: false,
        courses: false,
        request: false,
        vacancy: false,
        supportedJobSearch: false
    };
    const initialConstants = {
        displayESignature: false,
        requirePoNumberBeforeAppointmentInitialAttended: false,
        displayInitialAppointmentAccordion: false,
        initialAppointmentFields: [],
        automaticAppointments: []
    };
    const [sectionHeadings, setSectionHeadings] = useState(initialSectionHeadings);
    const [sectionHeadingsOnEntry, setSectionHeadingsOnEntry] = useState({});

    const [constants, setConstants] = useState(initialConstants);
    const [constantsOnEntry, setConstantsOnEntry] = useState({});

    const [arrayUserEventTypes, setArrayUserEventTypes] = useState([]);
    const [selectedUserEventTypeIds, setSelectedUserEventTypeIds] = useState([]);
    const [preSelectedUserEventTypeIds, setPreSelectedUserEventTypeIds] = useState([]);
    const [preSelectedUserEventTypes, setPreSelectedUserEventTypes] = useState([]);

    const [arrayAppointmentTypes, setArrayAppointmentTypes] = useState([]);
    const [selectedAppointmentTypeIds, setSelectedAppointmentTypeIds] = useState([]);
    const [preSelectedAppointmentTypeIds, setPreSelectedAppointmentTypeIds] = useState([]);
    const [preSelectedAppointmentTypes, setPreSelectedAppointmentTypes] = useState([]);

    const [preSelectedDocumentTypes, setPreSelectedDocumentTypes] = useState([]);
    const [preSelectedDocumentTypeIds, setPreSelectedDocumentTypeIds] = useState([]);

    const [selectedAutomaticAppointmentTypeIds, setSelectedAutomaticAppointmentTypeIds] = useState(
        []
    );
    const [preSelectedAutomaticAppointmentTypeIds, setPreSelectedAutomaticAppointmentTypeIds] =
        useState([]);
    const [preSelectedAutomaticAppointmentTypes, setPreSelectedAutomaticAppointmentTypes] =
        useState([]);

    const [errors, setErrors] = useState({});
    const [warnings, setWarnings] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [keys, setKeys] = useState({
        appointmentTypes: '0',
        initialAppointmentFields: '1',
        automaticAppointmentTypes: '2',
        userEventTypes: '3'
    });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const userEventTypes = useSelector((state) => state.entities.directusService.userEventTypes);
    const includedFieldsInInitialAppointment = useSelector(
        (state) => state.entities.directusService.includedFieldsInInitialAppointment
    );
    const documentTypes = useSelector(selectDocumentTypes);
    const appointmentSectionHeadings = useSelector(
        (state) => state.entities.participantService.appointmentSectionHeadings
    );
    const participantSectionHeadings = useSelector(
        (state) => state.entities.participantService.participantSectionHeadings
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) contractSetup();
    }, [contractId]);

    useEffect(() => {
        if (Object.keys(appointmentSectionHeadings)?.length > 0) {
            // eslint-disable-next-line
            const { id, contractId, ...rest } = appointmentSectionHeadings;
            setSectionHeadings((prev) => ({ ...prev, ...rest }));
            setSectionHeadingsOnEntry((prev) => ({ ...prev, ...rest }));
        }
    }, [appointmentSectionHeadings]);

    useEffect(() => {
        const existingConstants = {
            displayESignature: contractConstants?.displayESignature || false,
            requirePoNumberBeforeAppointmentInitialAttended:
                contractConstants.requirePoNumberBeforeAppointmentInitialAttended || false,
            displayInitialAppointmentAccordion:
                contractConstants?.displayInitialAppointmentAccordion,
            initialAppointmentFields: contractConstants?.initialAppointmentFields,
            automaticAppointments: contractConstants?.automaticAppointments || []
        };
        setConstants(existingConstants);
        setConstantsOnEntry(existingConstants);
    }, [contractConstants]);

    useEffect(() => {
        if (appointmentTypeDetails?.length > 0) {
            const { preSelects, preSelectIds } = updatePreSelects(
                appointmentTypeName,
                appointmentTypeDetails,
                contractId
            );
            setPreSelectedAppointmentTypes(preSelects);
            setPreSelectedAppointmentTypeIds(preSelectIds);
            setArrayAppointmentTypes([...appointmentTypeDetails]);
        }
    }, [appointmentTypeDetails]);

    useEffect(() => {
        if (userEventTypes?.length > 0) {
            const { preSelects, preSelectIds } = updatePreSelects(
                userEventTypeName,
                userEventTypes,
                contractId
            );
            setPreSelectedUserEventTypes(preSelects);
            setPreSelectedUserEventTypeIds(preSelectIds);
            setArrayUserEventTypes([...userEventTypes]);
        }
    }, [userEventTypes]);

    useEffect(() => {
        if ('automaticAppointments' in contractConstants) {
            const { automaticAppointments } = contractConstants;
            const updatedAutomaticAppointments = automaticAppointments.map((el) => ({
                ...el,
                active: preSelectedAppointmentTypeIds.includes(el.appointmentTypeId)
            }));
            setPreSelectedAutomaticAppointmentTypes(
                automaticAppointments?.length
                    ? automaticAppointments.map((el) => ({
                          id: el.appointmentTypeId,
                          name: el.appointmentType
                      }))
                    : []
            );
            setPreSelectedAutomaticAppointmentTypeIds(
                automaticAppointments?.length
                    ? automaticAppointments.map((el) => el.appointmentTypeId)
                    : []
            );
            setConstants((prev) => ({
                ...prev,
                automaticAppointments: updatedAutomaticAppointments
            }));
        }
    }, [preSelectedAppointmentTypeIds, contractConstants.automaticAppointments]);

    useEffect(() => {
        if (documentTypes?.length > 0) {
            const { preSelects, preSelectIds } = updatePreSelects(
                documentTypeName,
                documentTypes,
                contractId
            );
            setPreSelectedDocumentTypes(preSelects);
            setPreSelectedDocumentTypeIds(preSelectIds);
        }
    }, [documentTypes]);

    useEffect(() => {
        if (successMessage.includes(submitAppointmentTypesMessage)) {
            setSelectedAppointmentTypeIds([]);
            setPreSelectedAppointmentTypeIds([]);
            setPreSelectedDocumentTypes([]);
            setPreSelectedDocumentTypeIds([]);
            dispatch(loadAppointmentTypeDetails());
            dispatch(loadDocumentTypes());
        }
        if (successMessage.includes(submitUserEventTypesMessage)) {
            setSelectedUserEventTypeIds([]);
            setPreSelectedUserEventTypeIds([]);
            dispatch(loadUserEventTypes());
        }
        setIsDisabled(false);
    }, [successMessage]);

    useEffect(() => {
        setIsDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    //  HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (appointmentTypeDetails?.length < 1) return APPOINTMENT_TYPES_ERROR;
        if (userEventTypes?.length < 1) return USER_EVENT_TYPE_ERROR;
        if (Object.keys(contractConstants).length < 1) return CONTRACT_CONSTANTS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const contractSetup = () => {
        setSelectedUserEventTypeIds([]);
        setPreSelectedUserEventTypeIds([]);
        setSelectedAppointmentTypeIds([]);
        setPreSelectedAppointmentTypeIds([]);
        setPreSelectedDocumentTypes([]);
        setPreSelectedDocumentTypeIds([]);
        setKeys(clearKeys(keys));
        setWarnings([]);
        dispatch(clearAppointmentType());
        dispatch(loadAppointmentSectionHeadings(contractId));
        dispatch(loadContractConstants(contractId));
        dispatch(loadAppointmentTypeDetails());
        dispatch(loadUserEventTypes());
        dispatch(loadDocumentTypes());
        includedFieldsInInitialAppointment?.length < 1 &&
            dispatch(loadInCludedFieldsInInitialAppointment());
        setSectionHeadings(initialSectionHeadings);
        setConstants(initialConstants);
    };

    // EVENT HANDLERS
    const onUpdateConstants = (key, value) => {
        clearError(key);
        setConstants((prev) => ({ ...prev, [key]: value }));
    };

    const onAppointmentTypeChange = (chosenIds) => {
        if (!chosenIds) chosenIds = [];
        clearError('appointmentTypes');
        setSelectedAppointmentTypeIds(chosenIds);
    };

    const onUserEventTypeChange = (chosenIds) => {
        if (!chosenIds) chosenIds = [];
        clearError('userEventTypes');
        setSelectedUserEventTypeIds(chosenIds);
    };

    const onAutomaticAppointmentTypeChange = (chosenIds) => {
        let updatedAutomaticAppointments;
        const newIds = chosenIds
            .filter((el) => !preSelectedAutomaticAppointmentTypeIds.includes(el))
            .filter((el) => !selectedAutomaticAppointmentTypeIds.includes(el));
        if (newIds.length > 0) {
            updatedAutomaticAppointments = [
                ...newIds.map((el) => ({
                    id: `new-${uuid()}`,
                    appointmentTypeId: el,
                    active: true,
                    appointmentType:
                        preSelectedAppointmentTypes.find((entry) => entry.id === el)?.name || ''
                })),
                ...constants.automaticAppointments
            ];
        } else {
            const itemToRemove = constants.automaticAppointments.find(
                (el) => !chosenIds.includes(el.appointmentTypeId)
            );
            if (itemToRemove) {
                [`accordion-${itemToRemove.id}`, `automaticAppointment-${itemToRemove.id}`].forEach(
                    (el) => clearError(el)
                );
                const innerKeys = Object.keys(errors).filter((el) =>
                    el.startsWith(`automaticAppointment-${itemToRemove.id}`)
                );
                if (innerKeys.length) innerKeys.forEach((el) => clearError(el));
            }
            updatedAutomaticAppointments = constants.automaticAppointments.filter((el) =>
                chosenIds.includes(el.appointmentTypeId)
            );
        }
        setSelectedAutomaticAppointmentTypeIds(chosenIds);
        setConstants((prev) => ({ ...prev, automaticAppointments: updatedAutomaticAppointments }));
    };

    const onUpdateAutomaticAppointment = (updatedAppointment) => {
        setConstants((prev) => ({
            ...prev,
            automaticAppointments: constants.automaticAppointments.map((el) =>
                el.id === updatedAppointment.id ? updatedAppointment : el
            )
        }));
    };

    const onUpdateSectionHeadings = (key, e) => {
        setSectionHeadings((prev) => ({
            ...prev,
            [key]: e.target.checked
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsDisabled(true);
        let isUpdated = false;

        // Directus updates
        if (
            !deepEqual(selectedAppointmentTypeIds, preSelectedAppointmentTypeIds) ||
            selectedAppointmentTypeIds.length < 1 ||
            !deepEqual(selectedUserEventTypeIds, preSelectedUserEventTypeIds) ||
            selectedUserEventTypeIds < 1
        ) {
            let matchingAppointmentTypes = [];
            let nonMatchingAppointmentTypes = [];
            let updatedSelectedAppointmentTypeIds = [];
            let updatedDocumentTypeIds = [];
            let newWarnings = [];

            if (selectedAppointmentTypeIds.length > 0) {
                selectedAppointmentTypeIds
                    .map((el) => arrayAppointmentTypes.find((entry) => entry.id === el))
                    .forEach((el) =>
                        documentTypes.find(
                            (entry) => entry.name.toLowerCase() === el.name.toLowerCase()
                        )
                            ? (matchingAppointmentTypes = [...matchingAppointmentTypes, el])
                            : (nonMatchingAppointmentTypes = [...nonMatchingAppointmentTypes, el])
                    );
                newWarnings = configMissingDocumentTypes(nonMatchingAppointmentTypes);
                setWarnings(newWarnings);
                if (newWarnings.length > 0) {
                    setTimeout(() => setWarnings([]), _WARNING_TIMEOUT);
                }

                updatedSelectedAppointmentTypeIds = matchingAppointmentTypes.map((el) => el.id);
                updatedDocumentTypeIds = matchingAppointmentTypes.map(
                    (el) =>
                        documentTypes.find(
                            (entry) => entry.name.toLowerCase() === el.name.toLowerCase()
                        )?.id
                );
            }

            const newErrors = validateDirectusLists(
                updatedSelectedAppointmentTypeIds,
                selectedUserEventTypeIds
            );

            if (Object.keys(newErrors)?.length > 0) {
                setErrors(newErrors);
                return;
            }
            const isUpdatedDocumentTypes = addDirectusContractSelections(
                documentTypeName,
                preSelectedDocumentTypeIds,
                preSelectedDocumentTypes,
                updatedDocumentTypeIds,
                contractId,
                submitDocumentTypesMessage,
                dispatch
            );
            const isUpdatedAppointmentTypes = updateDirectusContractSelections(
                appointmentTypeName,
                preSelectedAppointmentTypeIds,
                preSelectedAppointmentTypes,
                updatedSelectedAppointmentTypeIds,
                contractId,
                submitAppointmentTypesMessage,
                dispatch
            );
            const isUpdatedUserEventTypes = updateDirectusContractSelections(
                userEventTypeName,
                preSelectedUserEventTypeIds,
                preSelectedUserEventTypes,
                selectedUserEventTypeIds,
                contractId,
                submitUserEventTypesMessage,
                dispatch
            );
            if (isUpdatedDocumentTypes || isUpdatedAppointmentTypes || isUpdatedUserEventTypes)
                isUpdated = true;
            else {
                if (newWarnings.length > 0) {
                    setSelectedAppointmentTypeIds(updatedSelectedAppointmentTypeIds);
                    setPreSelectedAppointmentTypeIds(updatedSelectedAppointmentTypeIds);
                    setPreSelectedAppointmentTypes(matchingAppointmentTypes);
                    setArrayAppointmentTypes(
                        arrayAppointmentTypes.filter(
                            (el) => !newWarnings.find((entry) => entry.id === el.id)
                        )
                    );
                }
            }
        }

        // Appointment SectionHeadings updates
        const isSame = deepEqual(sectionHeadings, sectionHeadingsOnEntry);
        if (!isSame) {
            if (
                await checkExists(
                    getParticipantURL() +
                        `rest/participant-section-heading/exists/${contractId}/appointment`
                )
            ) {
                const payload = {
                    id: appointmentSectionHeadings.id,
                    contractId,
                    type: 'appointment',
                    ...sectionHeadings
                };
                dispatch(updateAppointmentSectionHeadings(payload, submitSectionHeadingsMessage));
            } else {
                const payload = { id: null, contractId, type: 'appointment', ...sectionHeadings };
                dispatch(createAppointmentSectionHeadings(payload, submitSectionHeadingsMessage));
            }
            isUpdated = true;
            // check and update any ParticipantSectionHeadings that match above are also switched on
            let newMainHeadings = [];
            if (
                sectionHeadings.supportedJobSearch &&
                !participantSectionHeadings.supportedJobSearch
            )
                newMainHeadings = [...newMainHeadings, 'supportedJobSearch'];
            if (sectionHeadings?.barriers && !participantSectionHeadings.participantBarriers)
                newMainHeadings = [...newMainHeadings, 'participantBarriers'];
            if (sectionHeadings?.actionPlan && !participantSectionHeadings.actionPlanGoals)
                newMainHeadings = [...newMainHeadings, 'actionPlanGoals'];
            if (sectionHeadings?.request) {
                if (!participantSectionHeadings.financialRequests)
                    newMainHeadings = [...newMainHeadings, 'financialRequests'];
                if (!participantSectionHeadings.assetRequests)
                    newMainHeadings = [...newMainHeadings, 'assetRequests'];
            }
            if (sectionHeadings?.courses && !participantSectionHeadings.courses)
                newMainHeadings = [...newMainHeadings, 'courses'];
            if (sectionHeadings?.disclaimer && !participantSectionHeadings.disclaimer)
                newMainHeadings = [...newMainHeadings, 'disclaimer'];

            if (newMainHeadings.length > 0) {
                const updates = newMainHeadings.reduce((acc, cur) => ({ ...acc, [cur]: true }), {});
                if (
                    await checkExists(
                        getParticipantURL() +
                            'rest/participant-section-heading/exists/' +
                            contractId
                    )
                ) {
                    dispatch(
                        updateParticipantSectionHeadings({
                            contractId,
                            ...participantSectionHeadings,
                            ...updates
                        })
                    );
                } else
                    dispatch(
                        createParticipantSectionHeadings({ id: null, contractId, ...updates })
                    );
            }
        }

        // Contract Constants updates
        const newErrors = validateConstants(constants);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        } else if (!deepEqual(constants, constantsOnEntry)) {
            let updatedConstants = { ...constants };
            if (
                !deepEqual(constants.automaticAppointments, constantsOnEntry.automaticAppointments)
            ) {
                const updatedAutomaticAppointments = constants.automaticAppointments.map((el) => {
                    const updatedTriggers = el.triggers.map((entry) => ({
                        ...entry,
                        id: entry.id.startsWith('new-') ? '' : entry.id
                    }));
                    return {
                        ...el,
                        id: el.id.startsWith('new-') ? '' : el.id,
                        triggers: updatedTriggers
                    };
                });
                updatedConstants = {
                    ...constants,
                    automaticAppointments: updatedAutomaticAppointments
                };
            }
            dispatch(
                updateContractConstants(
                    {
                        ...contractConstants,
                        ...updatedConstants
                    },
                    submitContractConstantsMessage
                )
            );
            isUpdated = true;
        }
        if (!isUpdated)
            setErrors((prev) => ({ ...prev, button: { error: true, message: NO_UPDATE_ERROR } }));
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={local.appointmentsWrapper}>
            <form onSubmit={onSubmit} data-testid="appointments_admin_form_start">
                <div className={form.formSection} data-testid="initial_section">
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="userEventTypes"
                            key={keys.userEventTypes}
                            disabled={!hasRole(acceptedRoles, roles)}
                            label={'User Event Types'}
                            mandatory={true}
                            placeholder="Select user event types..."
                            menuItems={arrayUserEventTypes || []}
                            preSelectedIds={preSelectedUserEventTypeIds}
                            preSelects={preSelectedUserEventTypes}
                            error={errors.userEventTypes}
                            onChange={(chosenIds) => onUserEventTypeChange(chosenIds)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="appointmentTypes"
                            key={keys.appointmentTypes}
                            disabled={!hasRole(acceptedRoles, roles)}
                            label={'Appointment Types'}
                            mandatory={true}
                            placeholder="Select appointment types..."
                            menuItems={arrayAppointmentTypes || []}
                            preSelectedIds={preSelectedAppointmentTypeIds}
                            preSelects={preSelectedAppointmentTypes}
                            error={errors.appointmentTypes}
                            onChange={(chosenIds) => onAppointmentTypeChange(chosenIds)}
                        />
                        {warnings.length > 0 && (
                            <div
                                className={`${local.warningContent} ${app.fadein}`}
                                data-testid="warning_messages">
                                {warnings.map((el) => (
                                    <div key={el.id} className={app.warning}>
                                        The appointment type: <span>{el.name}</span> could not be
                                        added as it does not have a matching document type.
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className={form.formLabel}>
                    Select below what Accordions and fields will be displayed for all appointments
                    selected above
                </div>
                <div className={form.formSection} data-testid="header_switch_section">
                    <div className={form.formColumn}>
                        <div className={local.sectionHeadingRow}>
                            <span>Questionnaire</span>
                            <Switch
                                id="isQuestionnaire"
                                checked={sectionHeadings.questionnaire}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('questionnaire', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Job Readiness</span>
                            <Switch
                                id="isJobReadiness"
                                checked={sectionHeadings.jobReadiness}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('jobReadiness', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Action Plan</span>
                            <Switch
                                id="isActionPlan"
                                checked={sectionHeadings.actionPlan}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('actionPlan', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Barriers</span>
                            <Switch
                                id="isBarriers"
                                checked={sectionHeadings.barriers}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('barriers', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Disclaimer</span>
                            <Switch
                                id="isDisclaimer"
                                checked={sectionHeadings.disclaimer}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('disclaimer', e)}
                            />
                        </div>
                    </div>
                    <div className={form.formColumn}>
                        <div className={local.sectionHeadingRow}>
                            <span>Courses</span>
                            <Switch
                                id="isCourses"
                                checked={sectionHeadings.courses}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('courses', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Request</span>
                            <Switch
                                id="isRequest"
                                checked={sectionHeadings.request}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('request', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Vacancy</span>
                            <Switch
                                id="isVacancy"
                                checked={sectionHeadings.vacancy || false}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('vacancy', e)}
                            />
                        </div>
                        <div className={local.sectionHeadingRow}>
                            <span>Supported Job Search</span>
                            <Switch
                                id="isSupportedJobSearch"
                                checked={sectionHeadings.supportedJobSearch || false}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onOff={true}
                                inline={true}
                                onChange={(e) => onUpdateSectionHeadings('supportedJobSearch', e)}
                            />
                        </div>
                    </div>
                </div>

                <div className={form.formSection} data-testid="eSig_section">
                    <div className={form.formColumn}>
                        <div className={local.eSigRow}>
                            <div className={form.formLabel}> E-sign will be used</div>
                            <div className={local.sectionHeadingRow}>
                                <span>Display E-sign</span>
                                <Switch
                                    id="displayESignature"
                                    checked={constants.displayESignature || false}
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    onOff={true}
                                    inline={true}
                                    onChange={(e) =>
                                        onUpdateConstants('displayESignature', e.target.checked)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <InitialAppointmentConfiguration
                    constants={constants}
                    acceptedRoles={acceptedRoles}
                    errors={errors}
                    clearError={clearError}
                    keys={keys}
                    onUpdateConstants={onUpdateConstants}
                />

                <AutomaticAppointmentConfiguration
                    contractAppointmentTypes={preSelectedAppointmentTypes}
                    preSelectedAutomaticAppointmentTypeIds={preSelectedAutomaticAppointmentTypeIds}
                    preSelectedAutomaticAppointmentTypes={preSelectedAutomaticAppointmentTypes}
                    automaticAppointments={constants.automaticAppointments}
                    onAutomaticAppointmentTypeChange={onAutomaticAppointmentTypeChange}
                    onUpdateAutomaticAppointment={onUpdateAutomaticAppointment}
                    acceptedRoles={acceptedRoles}
                    errors={errors}
                    clearError={clearError}
                    keys={keys}
                />

                <Button
                    id="appointmentsAdminButton"
                    content="UPDATE APPOINTMENTS"
                    disabled={!hasRole(acceptedRoles, roles) || isDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

AppointmentsAdmin.propTypes = {
    contractId: PropTypes.string
};

export default AppointmentsAdmin;
